import { useCallback, useMemo, useState } from 'react';
import * as R from 'ramda';

import { GET_ASSETS_WITH_ELEMENTS } from '@atom/graph/asset';
import client from '@atom/graph/client';
import { useGroupedPagination } from '@atom/hooks/useGroupedPagination';
import { useInventoryCategoryTree } from '@atom/hooks/useInventoryCategoryTree';
import {
  AssetsWithElementsConnection,
  AssetsWithElementsInput,
  CategoryPathItem,
  InventoryAssetDetailType,
  InventoryCategoryTree,
  SearchAssetWithElements,
} from '@atom/types/inventory';
import { toggleFromSet } from '@atom/utilities/setUtilities';

import InventoryAssetSearch from './InventoryAssetSearch';
import SearchInput from './SearchInput';
import TreeContext, { Tree } from './TreeContext';
import TreeView from './TreeView';

import classes from './inventoryAssetTree.module.css';

const ASSETS_LIMIT = 5;

export interface FilterParams {
  /**
   * Denotes whether the tree should return only folders with published schema
   * associated to them. Used in category tree API call
   */
  onlyPublishedCategories?: boolean;
  /**
   * If defined only assets with the given schema will be rendered.
   */
  schemaId?: string;
  /**
   * If defined only assets with the given schema ids will be rendered.
   */
  includeInactive?: boolean;
  /**
   * Optional boolean value denoting whether to hide categories that do not contain active assets
   */
  hideEmptyCategories?: boolean;
  /**
   * If defined only assets with elements matching the given elementSchemaId will be returned
   */
  elementSchemaId?: string;
}

interface Props {
  /**
   * Collection of filter params that are used to filter assets and categories
   */
  filterParams?: FilterParams;
  /**
   * Optional callback to be called on asset click
   */
  onAssetClick?: (
    asset: SearchAssetWithElements | InventoryAssetDetailType,
    path: CategoryPathItem[],
  ) => void;
  /**
   * Optional callback called when a category is clicked.
   */
  onCategoryClick?: (
    category: InventoryCategoryTree,
    path?: CategoryPathItem[],
  ) => void;
  /**
   * string value that will be prefixed to any data-cy used within the common component
   * If not value is passed, prefix will be empty string
   */
  dataCyPrefix?: string;
  /**
   * id of asset to remove from the asset list. Transfer flows cannot select a destination on the same root asset.
   */
  removeAssetId: string;
}

export const isAsset = (tree: Tree): tree is InventoryAssetDetailType => {
  // @ts-ignore
  return !R.isNil(tree.markerId) || R.isNil(tree.hasAssets);
};

export const hasElements = (tree: Tree): boolean => {
  return (
    // @ts-ignore
    !!tree.elements?.length ||
    R.any(
      R.equals(true),
      // @ts-ignore
      tree.elementGroups?.map(item => !!item?.elements?.length),
    )
  );
};

const InventoryAssetTreeTransfer = ({
  onAssetClick,
  onCategoryClick,
  dataCyPrefix = '',
  filterParams,
  removeAssetId,
}: Props) => {
  const {
    schemaId,
    includeInactive = false,
    onlyPublishedCategories = true,
    hideEmptyCategories = true,
  }: FilterParams = filterParams;

  const [open, setOpen] = useState<Set<string>>(new Set(['inventory']));
  const [query, setQuery] = useState<string>('');

  const getAssetWithElements = (id: string, page: number, limit: number) => {
    return client.query<
      { assetsWithElements: AssetsWithElementsConnection },
      { input: AssetsWithElementsInput }
    >({
      query: GET_ASSETS_WITH_ELEMENTS,
      variables: {
        input: {
          schemaId: filterParams.elementSchemaId,
          categoryIds: [id],
          includeInactive,
          isVisibleAsSubtextAttributesOnly: true,
          page,
          limit,
        },
      },
      fetchPolicy: 'no-cache',
    });
  };

  const { fetchMore, getData, hasMore, isLoading, hasError } =
    useGroupedPagination<
      InventoryAssetDetailType | AssetsWithElementsConnection,
      any
    >({
      limit: ASSETS_LIMIT,
      fetcher: (id: string, page: number, limit: number) =>
        getAssetWithElements(id, page, limit),
      transformResponse: response => {
        return {
          data: response.data.assetsWithElements.assets,
          total: response.data.assetsWithElements.totalCount,
        };
      },
    });

  const showSearchResults = query.length >= 3;

  const [categoryTree, { loading: loadingTree }] = useInventoryCategoryTree({
    params: {
      schemaId,
      includeAssetInfo: true,
      onlyPublished: onlyPublishedCategories,
      hideEmptyCategories,
    },
  });

  const rootTree = useMemo(() => {
    if (!categoryTree) {
      return null;
    }

    return { ...categoryTree, name: 'Inventory' };
  }, [categoryTree]);

  const handleToggle = useCallback(
    (tree: Tree, path?: CategoryPathItem[]) => {
      const { id } = tree;
      const hasAssets = !isAsset(tree) && tree.hasAssets;

      // first time category with assets has been opened
      if (!open.has(id) && hasAssets && !getData(id).length) {
        fetchMore(id);
      }

      if (!isAsset(tree) && onCategoryClick) {
        onCategoryClick(tree, path);
      }

      setOpen(toggleFromSet(open, id));
    },
    [fetchMore, getData, onCategoryClick, setOpen],
  );

  const handleAssetClick = useCallback(
    (
      asset: InventoryAssetDetailType | SearchAssetWithElements,
      assetPath?: CategoryPathItem[],
    ) => {
      if (onAssetClick) {
        onAssetClick(asset, assetPath);
      }
    },
    [onAssetClick],
  );

  const contextValue = {
    fetchMore,
    getData,
    handleAssetClick,
    handleToggle,
    hasError,
    hasMore,
    isLoading,
    open,
    rootTree,
    filterParams,
    removeAssetId,
  };

  return (
    <TreeContext.Provider value={contextValue}>
      <div className={classes.container}>
        <SearchInput
          query={query}
          setQuery={setQuery}
          dataCyPrefix={dataCyPrefix}
        />
        {showSearchResults && (
          <InventoryAssetSearch
            includeInactive={includeInactive}
            query={query}
          />
        )}
        {!showSearchResults && (
          <TreeView level={0} loadingTree={loadingTree} trees={[rootTree]} />
        )}
      </div>
    </TreeContext.Provider>
  );
};

export default InventoryAssetTreeTransfer;
